import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/lib/picker-search";
import { Megaphone, Users, Package, Layers, Trash2, Plus, Search, ChevronRight } from "lucide-react";
import type { Campaign, Persona, Product, Narrative, Brand } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ShareDialog } from "@/components/share-dialog";

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  paused: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  archived: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
};

function getSnapshotCounts(campaign: Campaign) {
  try {
    const snapshot = campaign.notes ? JSON.parse(campaign.notes as string) : null;
    return {
      stages: snapshot?.funnelStrategy?.stages?.length ?? 0,
      templates: snapshot?.adUnitSelections?.length ?? 0,
      angles: snapshot?.narrativeAngles?.length ?? 0,
    };
  } catch {
    return { stages: 0, templates: 0, angles: 0 };
  }
}

export default function CampaignsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [filterPersona, setFilterPersona] = useState<string>("all");
  const [filterProduct, setFilterProduct] = useState<string>("all");
  const [search, setSearch] = useState("");

  const { data: brand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    enabled: !!brandId,
  });

  const { data: campaigns, isLoading } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`, "standard"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/campaigns?type=standard`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campaigns");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const { data: narratives } = useQuery<Narrative[]>({
    queryKey: [`/api/brands/${brandId}/narratives`],
    enabled: !!brandId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      await apiRequest("DELETE", `/api/campaigns/${campaignId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/campaigns`] });
      toast({ title: "Campaign deleted" });
      setDeleteTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Failed to delete campaign", description: e.message, variant: "destructive" });
    },
  });

  const personaMap = new Map(personas?.map((p) => [p.id, p]) || []);
  const productMap = new Map(products?.map((p) => [p.id, p]) || []);

  const filtered = campaigns?.filter(c => {
    if (filterPersona !== "all" && c.personaId !== filterPersona) return false;
    if (filterProduct !== "all" && c.productId !== filterProduct) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) ?? [];

  return (
    <>
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-campaigns-title">Campaigns</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Persona x Product x Narrative campaign plans</p>
          </div>
          <Button onClick={() => navigate(`/brands/${brandId}/campaign-wizard`)} className="rounded-full px-5" data-testid="button-create-campaigns">
            <Plus className="h-4 w-4 mr-1.5" /> New Campaign
          </Button>
        </div>

        <div className="flex items-center gap-3 flex-wrap fade-in-up-delay-1">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search campaigns..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="input-search-campaigns"
            />
          </div>
          <SearchableSelect
            value={filterPersona}
            onValueChange={setFilterPersona}
            placeholder="All Personas"
            testId="select-filter-persona"
            triggerClassName="w-[180px] rounded-full text-xs h-9"
            triggerIcon={<Users className="h-3 w-3 mr-1.5 text-muted-foreground shrink-0" />}
            options={[
              { value: "all", label: "All Personas" },
              ...(personas ?? []).map(p => ({ value: p.id, label: p.name })),
            ]}
          />
          <SearchableSelect
            value={filterProduct}
            onValueChange={setFilterProduct}
            placeholder="All Products"
            testId="select-filter-product"
            triggerClassName="w-[180px] rounded-full text-xs h-9"
            triggerIcon={<Package className="h-3 w-3 mr-1.5 text-muted-foreground shrink-0" />}
            options={[
              { value: "all", label: "All Products" },
              ...(products ?? []).map(p => ({ value: p.id, label: p.name })),
            ]}
          />
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="rounded-2xl border border-border/40 overflow-hidden bg-card fade-in-up-delay-2" data-testid="table-campaigns">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border/30 bg-muted/30">
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-5 py-3">Campaign</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3 hidden md:table-cell">Persona</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3 hidden md:table-cell">Product</th>
                  <th className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3 hidden lg:table-cell">Stages</th>
                  <th className="text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3 hidden lg:table-cell">Templates</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3">Status</th>
                  <th className="text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 py-3 hidden sm:table-cell">Created</th>
                  <th className="px-3 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((campaign) => {
                  const persona = campaign.personaId ? personaMap.get(campaign.personaId) : null;
                  const product = campaign.productId ? productMap.get(campaign.productId) : null;
                  const counts = getSnapshotCounts(campaign);

                  return (
                    <tr
                      key={campaign.id}
                      className="border-b border-border/20 last:border-b-0 hover:bg-muted/20 cursor-pointer transition-colors group"
                      onClick={() => {
                        if (campaign.status === "draft") {
                          navigate(`/brands/${brandId}/campaign-wizard/${campaign.id}`);
                        } else {
                          navigate(`/brands/${brandId}/campaigns/${campaign.id}`);
                        }
                      }}
                      data-testid={`row-campaign-${campaign.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-snug line-clamp-1" data-testid={`text-campaign-name-${campaign.id}`}>
                            {campaign.name}
                          </p>
                          {campaign.objective && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug whitespace-normal break-words" data-testid={`text-campaign-objective-${campaign.id}`}>
                              {campaign.objective}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        {persona ? (
                          <div className="flex items-center gap-2">
                            <Users className="h-3 w-3 shrink-0 text-violet-500/60" />
                            <span className="text-xs text-foreground/80 truncate max-w-[140px]">{persona.name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        {product ? (
                          <div className="flex items-center gap-2">
                            <Package className="h-3 w-3 shrink-0 text-blue-500/60" />
                            <span className="text-xs text-foreground/80 truncate max-w-[140px]">{product.name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center hidden lg:table-cell">
                        {counts.stages > 0 ? (
                          <span className="text-xs font-medium text-foreground">{counts.stages}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center hidden lg:table-cell">
                        {counts.templates > 0 ? (
                          <span className="text-xs font-medium text-foreground">{counts.templates}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground/40">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${statusColors[campaign.status] || statusColors.draft}`} data-testid={`badge-status-${campaign.id}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 hidden sm:table-cell">
                        {campaign.createdAt && (
                          <span className="text-[11px] text-muted-foreground/60" data-testid={`date-created-${campaign.id}`}>
                            {new Date(campaign.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3.5">
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          {brand?.workspaceId && (
                            <ShareDialog
                              resourceType="campaign"
                              resourceId={campaign.id}
                              resourceName={campaign.name}
                              workspaceId={brand.workspaceId}
                              iconOnly
                            />
                          )}
                          <button
                            type="button"
                            className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteTarget(campaign);
                            }}
                            data-testid={`button-delete-campaign-${campaign.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <Card className="border-dashed border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Megaphone className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold tracking-tight mb-1">No campaigns yet</h3>
              <p className="text-sm text-muted-foreground">Get started by creating your first one</p>
            </CardContent>
          </Card>
        )}
      </div>

      {deleteTarget && (
        <Dialog open onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTarget(null); }}>
          <DialogContent data-testid="dialog-delete-campaign">
            <DialogHeader>
              <DialogTitle>Delete Campaign</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This will permanently remove the campaign and all its associated creative outputs and funnel plans. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                data-testid="button-cancel-delete"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
                data-testid="button-confirm-delete"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
